import {
  BrowserAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type RedirectRequest,
  type SilentRequest
} from "@azure/msal-browser";
import { isPopupBlocked } from "@/helpers/auth";
import type { AuthProvider, AuthProviderConfig } from "@ics/auth-api";
import { selectProviderAccount } from "./selectProviderAccount";

const debugPrefix = "[B2CProvider]";

// WI 2211: rapporter nettleser-side innloggingsfeil til auth-loggen (best-effort). Lazy `import()`
// for å unngå sirkulær import-syklus (B2CProvider ← providerRegistry ← useAuthShell ← axiosClient ←
// endpoints/authAudit → store → ...). Rapportering skal aldri bryte innloggingsflyten.
function reportLoginFailedBestEffort (payload: { errorCode?: string; reason?: string; provider?: string }): void {
  void import("@/store/authAuditClientStore")
    .then(m => m.useAuthAuditClientStore().reportLoginFailed(payload))
    .catch(() => { /* best-effort */ });
}

const POPUP_FAILURE_CODES = [
  "popup_window_error",
  "user_cancelled",
  "hash_empty_error",
  "timed_out"
];

// MSAL default popupBridgeTimeout er 60s, som er total tid fra popup åpnes
// til BroadcastChannel-respons fra redirect.html — inkludert brukerens MFA.
// For inkognito eller førstegangs-pålogging uten sparte cookies/passord
// overskrides 60s lett. 10 min gir realistisk slingringsmonn uten å henge
// for evig hvis popup'en faktisk er død (lukket-av-bruker fanges av eget
// popup-event, ikke av denne timeren).
const POPUP_BRIDGE_TIMEOUT_MS = 10 * 60 * 1000;

function isPopupFailure (error: unknown): boolean {
  if (error instanceof BrowserAuthError) {
    return POPUP_FAILURE_CODES.some(code => error.errorCode?.includes(code) || error.message?.includes(code));
  }
  return false;
}

// Hengende «interaksjon pågår»-flagg i nettleserlagringen — typisk etter et
// avbrutt/blokkert forsøk eller en redirect-retur som aldri ble håndtert.
// Skal ryddes og forsøkes på nytt, ikke feile hardt (WI 2113).
const INTERACTION_IN_PROGRESS_CODE = "interaction_in_progress";

function isInteractionInProgress (error: unknown): boolean {
  if (error instanceof BrowserAuthError) {
    return Boolean(error.errorCode?.includes(INTERACTION_IN_PROGRESS_CODE) || error.message?.includes(INTERACTION_IN_PROGRESS_CODE));
  }
  return false;
}

// authority_mismatch (ClientConfigurationError, ikke BrowserAuthError): stale
// konto-/authority-state i cachen matcher ikke konfigurert authority etter en
// config-/utrullingsendring. Skal ryddes og forsøkes på nytt automatisk (WI 2191).
const AUTHORITY_MISMATCH_CODE = "authority_mismatch";

function isAuthorityMismatch (error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const errorCode = (error as { errorCode?: string }).errorCode;
  return errorCode === AUTHORITY_MISMATCH_CODE
    || error.name === "ClientConfigurationError"
    || error.message.includes(AUTHORITY_MISMATCH_CODE);
}

/**
 * "External tenant"-provider — håndterer både Microsoft Entra External ID
 * (`<tenant>.ciamlogin.com`, modernisert) og klassisk Azure AD B2C
 * (`<tenant>.b2clogin.com` med B2C_1_*-user-flow). Klassenavnet er bevart
 * av historiske grunner (`type: "b2c"` brukes som intern label i shell-en).
 *
 * Egen MSAL-instans med authority + knownAuthorities fra config.
 * `knownAuthorities` er kritisk for B2C; for Entra External ID ikke
 * strengt påkrevd, men skadefritt å sette likevel (gir konsistent
 * MSAL-validerings-flyt).
 *
 * Til forskjell fra EntraProvider deler ikke denne MSAL-instans med
 * helpers/auth.ts. Hver provider har sin egen cache i localStorage; logout
 * av Entra rør ikke denne providers cache og omvendt.
 *
 * Config kommer fra /api/config (#1196):
 * - Entra External ID: authority `https://<tenant>.ciamlogin.com/<tenant>.onmicrosoft.com/v2.0`
 * - Klassisk B2C:      authority `https://<tenant>.b2clogin.com/<tenant>.onmicrosoft.com/<userflow>`
 */
export class B2CProvider implements AuthProvider {
  readonly id: string;
  readonly displayName: string;
  readonly type = "b2c" as const;

  private readonly scopes: string[];
  private readonly authority: string;
  private readonly knownAuthorities: string[];
  private readonly clientId: string;
  private msalInstance: PublicClientApplication | null = null;
  private initPromise: Promise<PublicClientApplication> | null = null;

  // Deduplisering av parallelle token-forespørsler. Uten dette trigger 3
  // samtidige API-kall 3 popups som kolliderer med MSAL `interaction_in_progress`.
  // Mønster speiler legacy `requestToken` i helpers/auth.ts.
  private inflightSilentOrPopup: Promise<AuthenticationResult | null> | null = null;
  private inflightSilent: Promise<AuthenticationResult | null> | null = null;
  private cachedToken: AuthenticationResult | null = null;
  private cachedExpiresAt = 0;
  private static readonly tokenBufferMs = 5 * 60 * 1000;

  constructor (config: AuthProviderConfig) {
    if (config.type !== "b2c") {
      throw new Error(`B2CProvider kan ikke konstrueres med type '${config.type}'`);
    }
    if (!config.knownAuthorities || config.knownAuthorities.length === 0) {
      throw new Error(`B2CProvider '${config.id}' mangler knownAuthorities — påkrevd for B2C-token-validering`);
    }

    this.id = config.id;
    this.displayName = config.displayName;
    this.clientId = config.clientId;
    this.authority = config.authority;
    this.knownAuthorities = config.knownAuthorities;
    this.scopes = config.scopes;
  }

  async login (): Promise<boolean> {
    if (isPopupBlocked.value) {
      // Popup er allerede kjent som blokkert — gå rett til redirect-fallback
      // for å unngå unødvendig feilet popup-forsøk.
      await this.loginRedirect();
      return false;
    }

    try {
      // WI 2448: varm instans synkront når mulig, så `loginPopup` fyres uten en
      // mellomliggende `await` fra klikk-gesturet (bevarer «user activation»).
      const instance = this.getInstanceSync() ?? await this.getInstance();
      const response = await instance.loginPopup({
        scopes: this.scopes,
        prompt: "select_account"
      });

      if (response?.account) {
        instance.setActiveAccount(response.account);
        return true;
      }
      return false;
    } catch (error) {
      if (isInteractionInProgress(error)) {
        return await this.recoverLoginFromHangingInteraction();
      }
      if (isAuthorityMismatch(error)) {
        return await this.recoverLoginFromAuthorityMismatch();
      }
      if (isPopupFailure(error)) {
        console.warn(`${debugPrefix} Popup feilet — bytter til redirect-flow:`, error);
        reportLoginFailedBestEffort({ errorCode: 'popup_blocked', reason: (error as { message?: string })?.message, provider: 'b2c' });
        await this.loginRedirect();
        return false;
      }
      console.error(`${debugPrefix} Login feilet:`, error);
      reportLoginFailedBestEffort({
        errorCode: (error as { errorCode?: string })?.errorCode ?? 'login_error',
        reason: (error as { message?: string })?.message ?? String(error),
        provider: 'b2c'
      });
      return false;
    }
  }

  /**
   * Recovery for `interaction_in_progress` (WI 2113): la MSAL sluttføre/rydde
   * den hengende interaksjonen via `handleRedirectPromise()`. Lå det en
   * uhåndtert redirect-retur i lagringen fullføres påloggingen direkte;
   * ellers gjøres ETT nytt popup-forsøk, med redirect som siste fallback.
   * Reelt pågående interaksjoner i samme fane når aldri hit — parallelle
   * token-kall dedupliseres via inflight-promisene.
   */
  private async recoverLoginFromHangingInteraction (): Promise<boolean> {
    const instance = await this.getInstance();
    console.warn(`${debugPrefix} interaction_in_progress — rydder hengende interaksjons-state og prøver på nytt`);

    try {
      const pending = await instance.handleRedirectPromise();
      if (pending?.account) {
        instance.setActiveAccount(pending.account);
        return true;
      }
    } catch (cleanupError) {
      // MSAL rydder interaksjons-flagget også når sluttføringen feiler.
      console.warn(`${debugPrefix} Opprydding av hengende interaksjon feilet:`, cleanupError);
    }

    // `handleRedirectPromise()` rydder IKKE det hengende interaction_in_progress-
    // flagget når URL-en mangler et auth-svar (vanlig stuck-tilfelle: et avbrutt
    // forsøk). Uten eksplisitt tømming feiler retryen under med samme flagg og
    // alle senere innlogginger låses. `clearCache()` fjerner flagget (WI 2118).
    try {
      await instance.clearCache();
    } catch (clearError) {
      console.warn(`${debugPrefix} clearCache under interaksjons-opprydding feilet:`, clearError);
    }

    try {
      const response = await instance.loginPopup({
        scopes: this.scopes,
        prompt: "select_account"
      });
      if (response?.account) {
        instance.setActiveAccount(response.account);
        return true;
      }
      return false;
    } catch (retryError) {
      console.warn(`${debugPrefix} Popup feilet etter opprydding — bytter til redirect-flow:`, retryError);
      await this.loginRedirect();
      return false;
    }
  }

  /**
   * Recovery for `authority_mismatch` (WI 2191): tøm den utdaterte cachen og gjør
   * ÉTT nytt popup-forsøk. `clearCache()` rører kun denne providerens egen
   * localStorage-partisjon (clientId-scopet), ikke Entra-providerens. Vedvarende
   * mismatch (ikke stale cache) gir `false` etter ett forsøk — ingen løkke, og
   * ingen redirect-fallback (samme authority-config ville feilet likt).
   */
  private async recoverLoginFromAuthorityMismatch (): Promise<boolean> {
    const instance = await this.getInstance();
    console.warn(`${debugPrefix} authority_mismatch — rydder stale cache og prøver på nytt`);

    try {
      await instance.clearCache();
    } catch (clearError) {
      console.warn(`${debugPrefix} clearCache under authority_mismatch-recovery feilet:`, clearError);
    }

    try {
      const response = await instance.loginPopup({
        scopes: this.scopes,
        prompt: "select_account"
      });
      if (response?.account) {
        instance.setActiveAccount(response.account);
        return true;
      }
      return false;
    } catch (retryError) {
      console.error(`${debugPrefix} Login feilet etter authority_mismatch-recovery:`, retryError);
      return false;
    }
  }

  async loginRedirect (_intendedRoute?: string): Promise<void> {
    const instance = await this.getInstance();
    const baseUri = location.origin.endsWith("/") ? location.origin : `${location.origin}/`;
    const request: RedirectRequest = {
      scopes: this.scopes,
      prompt: "select_account",
      redirectStartPage: baseUri
    };
    try {
      await instance.loginRedirect(request);
    } catch (error) {
      if (!isInteractionInProgress(error)) {
        throw error;
      }
      // Samme hengende tilstand som i login(): rydd og prøv én gang til, slik
      // at «Logg inn med redirect» fra popup-varselet ikke feiler hardt (WI 2113).
      console.warn(`${debugPrefix} interaction_in_progress ved redirect-start — rydder og prøver på nytt`);
      try {
        await instance.handleRedirectPromise();
      } catch (cleanupError) {
        console.warn(`${debugPrefix} Opprydding av hengende interaksjon feilet:`, cleanupError);
      }
      // Tøm hengende interaction_in_progress-flagg eksplisitt (WI 2118) — se
      // recoverLoginFromHangingInteraction; handleRedirectPromise rydder det ikke
      // uten et auth-svar i URL-en.
      try {
        await instance.clearCache();
      } catch (clearError) {
        console.warn(`${debugPrefix} clearCache under interaksjons-opprydding feilet:`, clearError);
      }
      await instance.loginRedirect(request);
    }
  }

  async handleRedirectResponse (): Promise<string | null> {
    const instance = await this.getInstance();
    // Redirect-loop-vern (WI 2118): appen eier navigasjonen etter redirect-retur
    // (jf. 2113), så MSAL skal IKKE auto-navigere tilbake til opprinnelig
    // forespørsels-URL. I msal-browser v5 settes dette via
    // handleRedirectPromise-options (det gamle config-feltet er fjernet i v5).
    const response = await instance.handleRedirectPromise({
      hash: window.location.hash,
      navigateToLoginRequestUrl: false
    });

    if (response?.account) {
      instance.setActiveAccount(response.account);
      return "/";
    }
    return null;
  }

  async logout (): Promise<void> {
    if (!this.msalInstance) {
      return;
    }
    await this.msalInstance.logoutPopup({ mainWindowRedirectUri: "/" });
  }

  async isAuthenticated (): Promise<boolean> {
    const instance = await this.getInstance();
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
      return false;
    }
    instance.setActiveAccount(accounts[0]);
    try {
      await instance.acquireTokenSilent({ account: accounts[0], scopes: this.scopes });
      return true;
    } catch {
      return false;
    }
  }

  async restore (): Promise<boolean> {
    try {
      const instance = await this.getInstance();
      // WI 2291: MSAL deler konto-registeret på tvers av providere på samme
      // origin. Velg kontoen som faktisk hører til DENNE provideren — ikke
      // `accounts[0]`, som er ikke-deterministisk og kan være Entra-providerens
      // konto (→ «unknown»-bruker eller innloggingsskjerm ved reload).
      const account = selectProviderAccount(
        instance.getAllAccounts(),
        { authority: this.authority, knownAuthorities: this.knownAuthorities },
        instance.getActiveAccount()
      );
      if (!account) {
        return false;
      }
      instance.setActiveAccount(account);
      const result = await instance.acquireTokenSilent({
        account,
        scopes: this.scopes
      });
      if (result?.accessToken) {
        this.rememberToken(result);
        return true;
      }
      return false;
    } catch (error) {
      console.debug(`${debugPrefix} Silent restore feilet:`, error);
      return false;
    }
  }

  currentAccount (): AccountInfo | null {
    return this.msalInstance?.getActiveAccount() ?? null;
  }

  async getTokenSilentOrPopup (): Promise<AuthenticationResult | null> {
    if (this.isCachedTokenValid()) {
      return this.cachedToken;
    }
    this.inflightSilentOrPopup ??= this.requestTokenWithPopupFallback();
    try {
      return await this.inflightSilentOrPopup;
    } finally {
      this.inflightSilentOrPopup = null;
    }
  }

  async getTokenSilent (): Promise<AuthenticationResult | null> {
    if (this.isCachedTokenValid()) {
      return this.cachedToken;
    }
    this.inflightSilent ??= this.requestTokenSilentOnly();
    try {
      return await this.inflightSilent;
    } finally {
      this.inflightSilent = null;
    }
  }

  private isCachedTokenValid (): boolean {
    return this.cachedToken !== null && Date.now() < this.cachedExpiresAt;
  }

  private rememberToken (token: AuthenticationResult): void {
    this.cachedToken = token;
    const expiresOn = token.expiresOn?.getTime() ?? Date.now() + 60 * 60 * 1000;
    this.cachedExpiresAt = expiresOn - B2CProvider.tokenBufferMs;
  }

  private async requestTokenWithPopupFallback (): Promise<AuthenticationResult | null> {
    const instance = await this.getInstance();
    const account = instance.getActiveAccount();
    const request: SilentRequest = {
      account: account ?? undefined,
      scopes: this.scopes
    };

    if (account) {
      try {
        const silent = await instance.acquireTokenSilent(request);
        if (silent?.accessToken) {
          this.rememberToken(silent);
          return silent;
        }
      } catch (silentError) {
        console.debug(`${debugPrefix} Silent token feilet, prøver popup:`, silentError);
      }
    }

    try {
      const popupResult = await instance.acquireTokenPopup({
        ...request,
        prompt: "select_account"
      });
      if (popupResult?.accessToken) {
        this.rememberToken(popupResult);
        return popupResult;
      }
      return null;
    } catch (popupError) {
      console.error(`${debugPrefix} Token-popup feilet:`, popupError);
      return null;
    }
  }

  private async requestTokenSilentOnly (): Promise<AuthenticationResult | null> {
    const instance = await this.getInstance();
    const account = instance.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const silent = await instance.acquireTokenSilent({
        account,
        scopes: this.scopes
      });
      if (silent?.accessToken) {
        this.rememberToken(silent);
      }
      return silent;
    } catch (error) {
      console.debug(`${debugPrefix} Silent refresh feilet:`, error);
      return null;
    }
  }

  private async getInstance (): Promise<PublicClientApplication> {
    this.initPromise ??= this.buildInstance();
    return await this.initPromise;
  }

  /**
   * Synkron tilgang til en allerede bygget MSAL-instans (WI 2448). Returnerer
   * `null` hvis instansen ennå ikke er varm. Brukes av `login()` for å fyre
   * `loginPopup` synkront i klikk-gesturet — en `await getInstance()` mellom
   * klikk og popup mister «user activation» i strenge nettlesere (Safari).
   */
  private getInstanceSync (): PublicClientApplication | null {
    return this.msalInstance;
  }

  private async buildInstance (): Promise<PublicClientApplication> {
    if (this.msalInstance) {
      return this.msalInstance;
    }

    const baseUri = location.origin.endsWith("/") ? location.origin : `${location.origin}/`;
    const instance = new PublicClientApplication({
      auth: {
        clientId: this.clientId,
        authority: this.authority,
        knownAuthorities: this.knownAuthorities,
        redirectUri: `${baseUri}redirect.html`,
        // Lukke-vennlig logout-side (WI 2197): app-roten lastet hele SPA-en i
        // logout-popup-en så den aldri lukket seg. logout.html er en minimal
        // same-origin-side som lar MSAL lukke popup-en (paritet med Entra i auth.ts).
        postLogoutRedirectUri: `${baseUri}logout.html`
        // Redirect-loop-vern settes via handleRedirectPromise({ navigateToLoginRequestUrl:
        // false }) i handleRedirectResponse() — config-feltet finnes ikke i msal-browser v5.
      },
      cache: {
        cacheLocation: "localStorage"
      },
      system: {
        popupBridgeTimeout: POPUP_BRIDGE_TIMEOUT_MS
      }
    });
    await instance.initialize();
    this.msalInstance = instance;
    return instance;
  }
}
